import classNames from 'classnames';
import React from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { getSpecificPortfolioV3 } from 'venn-api';
import { proxySetIdState } from 'venn-state';
import { Label, Loading, LoadingSize, SecondaryLabel, selectClasses } from 'venn-ui-kit';
import type { BasePortfolioField } from './types';
import { portfolioToSearchMenuItem, SearchMenuBar, type SearchMenuItem } from '../../search-menu';
import { CreateFromScratchFooter } from '../../search-menu/components/CreateCompositeFooter';
import { isNil } from 'lodash';
import { PORTFOLIO_LAB_OPTIONS_NEGATIVE_ALLOCATIONS_CASH_POSITIONS_DISABLED_MESSAGE } from 'venn-utils';

export const PORTFOLIOS_WITH_HISTORICAL_ALLOCATIONS_NOT_SUPPORTED =
  'Portfolios with historical allocations are not supported at this time.';
export const getDisabledPortfolioTooltipMessage = (item: SearchMenuItem) => {
  if (item.value?.isHistoricalPortfolio || item.value?.portfolio?.historical) {
    return PORTFOLIOS_WITH_HISTORICAL_ALLOCATIONS_NOT_SUPPORTED;
  }
  if (item.value?.isLeverageShort || item.value?.hasOptions) {
    return PORTFOLIO_LAB_OPTIONS_NEGATIVE_ALLOCATIONS_CASH_POSITIONS_DISABLED_MESSAGE;
  }
  return '';
};

const Wrapper = styled.div`
  margin-top: 20px;
`;

const qaClass = 'qa-create-portfolio-from';

export type BasePortfolioStateUpdater = (state: BasePortfolioField) => BasePortfolioField;
export type BasePortfolioChangeCallback = () => void;

export interface BasePortfolioProps {
  basePortfolio: BasePortfolioField;
  onChange(updater: BasePortfolioStateUpdater, callback?: BasePortfolioChangeCallback): void;
  placeholder?: string;
  label?: string;
  secondaryLabel?: string;
  className?: string;
  refreshedStyling?: boolean;
  showClearButton?: boolean;
}

const BasePortfolio = ({
  onChange,
  placeholder,
  label,
  secondaryLabel,
  basePortfolio,
  className,
  refreshedStyling,
  showClearButton = false,
}: BasePortfolioProps) => {
  const { loading, disabled: baseDisabled, allowFromScratch, baseline } = basePortfolio;
  const proxySetId = useRecoilValue(proxySetIdState);

  const toggleLoading = (newLoading: boolean) =>
    onChange((prevState) => ({
      ...prevState,
      loading: newLoading,
    }));

  const startLoading = () => toggleLoading(true);

  const fetchSpecificPortfolio = async (id: number | undefined) => {
    startLoading();

    if (!id) {
      onChange((prevState) => ({
        ...prevState,
        loading: false,
        baseline: undefined,
      }));
      return;
    }

    try {
      const portfolio = (await getSpecificPortfolioV3(id, undefined, proxySetId)).content;
      onChange((prevState) => ({
        ...prevState,
        loading: false,
        baseline: portfolio,
      }));
    } catch (error) {
      onChange((prevState) => ({
        ...prevState,
        loading: false,
      }));
    }
  };

  const onLocalChange = (id: number | undefined) => {
    onChange(
      (prevState) => ({
        ...prevState,
        id,
      }),
      () => fetchSpecificPortfolio(id),
    );
  };

  const onCreateFromScratch = () => {
    onLocalChange(undefined);
  };

  return (
    <Wrapper className={classNames(className, qaClass)}>
      {loading ? (
        <Loading size={LoadingSize.small} />
      ) : (
        <>
          <Label>{label ?? 'Create new portfolio from:'}</Label>
          {secondaryLabel && <SecondaryLabel>{secondaryLabel}</SecondaryLabel>}
          <StyledSearchMenuBar
            refreshedStyling={refreshedStyling}
            smallScreen
            autofocus={false}
            customPlaceholder={placeholder ?? 'Create from scratch...'}
            value={baseline ? portfolioToSearchMenuItem(baseline, proxySetId) : null}
            isOptionDisabled={(item) => isNil(item.value?.portfolio) || !!item.value?.portfolio?.historical}
            optionDisabledTooltipContent={getDisabledPortfolioTooltipMessage}
            isClearable
            portfoliosOnly
            onSelected={(item) => onLocalChange(item.value?.portfolio?.id)}
            location="Add to portfolio"
            footer={
              allowFromScratch ? CreateFromScratchFooter(onCreateFromScratch, refreshedStyling ?? false) : undefined
            }
            disabled={baseDisabled}
            onClear={
              showClearButton
                ? () => {
                    onLocalChange(undefined);
                  }
                : undefined
            }
            privateAssetSearchMode="PUBLIC_ONLY"
          />
        </>
      )}
    </Wrapper>
  );
};

export default BasePortfolio;

const StyledSearchMenuBar = styled(SearchMenuBar)`
  .${selectClasses.SearchIcon} {
    display: none;
  }
`;
